/**
 * Curated sensor-name suggestions for the onboarding "name your sensor" step.
 *
 * Mined from the ~278k sensor names in production (July 2026), keeping the
 * names that are generic enough to be reusable and dropping the long tail of
 * one-offs (place names, people's names, appliance brands, and the "delete me"
 * / "test" / "not in use" junk). QUICK_PICKS are the highest-volume names and
 * are shown as tappable buttons; everything in SUGGESTIONS is reachable from
 * the free-text field's autocomplete.
 *
 * Selective (fire-sprinkler) sensors get their own lists — once a sensor has
 * been scanned we know it's a Selective unit, so the presets can talk about
 * branch lines and riser rooms instead of freezers.
 */

// Ordered by real-world popularity. Eight fits a 2-column grid on a phone
// without pushing the free-text field below the fold.
export const QUICK_PICKS = [
  'Freezer',
  'Fridge',
  'Garage',
  'Basement',
  'Greenhouse',
  'Cooler',
  'Server Room',
  'Office',
];

// Branch lines lead: they're the placement we recommend on the pipe-strap
// step, so they should be the first thing offered here too. Riser follows.
// 'Branch Line' and 'Riser Room' double as the presets the naming step
// pre-selects from the pipe-strap answer (see util/selective-pipe-strap.js),
// so keep both present. The rest are the freeze-prone spots sprinkler
// installs monitor most.
export const SELECTIVE_QUICK_PICKS = [
  'Branch Line',
  'Riser Room',
  'Attic',
  'Loading Dock',
  'Stairwell',
  'Parking Garage',
];

// Fire-sprinkler vocabulary. Listed first in the autocomplete pool for
// Selective sensors so "bra" surfaces "Branch Line" before "Break Room".
export const SELECTIVE_SUGGESTIONS = [
  'Branch Line',
  'Attic Branch Line',
  'Basement Branch Line',
  'Office Branch Line',
  'Warehouse Branch Line',
  'Stairwell Branch Line',
  'Loading Dock Branch Line',
  'Parking Garage Branch Line',
  'Canopy Branch Line',
  'Riser Room',
  'Riser Pipe',
  'Sprinkler Riser',
  'Wet Pipe Riser',
  'Sprinkler Room',
  'Fire Pump Room',
  'Dry Pipe Valve',
  'Stairwell',
  'Parking Garage',
  'Loading Dock',
  'Attic',
  'Crawl Space',
  'Mechanical Room',
  'Electrical Room',
  'Boiler Room',
  'Warehouse',
  'Office',
  'Hallway',
  'Lobby',
  'Basement',
  'Penthouse',
  'Roof',
  'Canopy',
  'Exterior Wall',
  'Loading Bay',
  'Cold Storage',
  'Freezer Room',
];

export const SUGGESTIONS = [
  // ── Freezers ───────────────────────────────────────────────────────────────
  'Freezer',
  'Chest Freezer',
  'Upright Freezer',
  'Deep Freezer',
  'Walk-In Freezer',
  'Reach-In Freezer',
  'Display Freezer',
  'Blast Freezer',
  'Garage Freezer',
  'Basement Freezer',
  'Kitchen Freezer',
  'Shop Freezer',
  'Outside Freezer',
  'Warehouse Freezer',
  'Ice Cream Freezer',
  'Meat Freezer',
  'Bakery Freezer',
  'Small Freezer',
  'Large Freezer',
  'Backup Freezer',

  // ── Fridges ────────────────────────────────────────────────────────────────
  'Fridge',
  'Refrigerator',
  'Garage Fridge',
  'Kitchen Fridge',
  'Basement Fridge',
  'Office Fridge',
  'Mini Fridge',
  'Beer Fridge',
  'Wine Fridge',
  'Bar Fridge',
  'Milk Fridge',
  'Walk-In Fridge',

  // ── Coolers & cases ────────────────────────────────────────────────────────
  'Cooler',
  'Walk-In Cooler',
  'Walk-In',
  'Beer Cooler',
  'Meat Cooler',
  'Milk Cooler',
  'Dairy Cooler',
  'Produce Cooler',
  'Deli Cooler',
  'Flower Cooler',
  'Keg Cooler',
  'Prep Cooler',
  'Display Cooler',
  'Wine Cooler',
  'Salad Cooler',
  'Deli Case',
  'Meat Case',
  'Produce Case',
  'Dairy Case',
  'Cheese Case',
  'Cake Display',
  'Dipping Cabinet',
  'Meat Locker',
  'Cold Room',
  'Cold Storage',
  'Freezer Room',

  // ── Rooms in a home ────────────────────────────────────────────────────────
  'Kitchen',
  'Living Room',
  'Dining Room',
  'Family Room',
  'Great Room',
  'Bedroom',
  'Master Bedroom',
  'Guest Bedroom',
  'Nursery',
  'Bathroom',
  'Master Bath',
  'Hallway',
  'Laundry Room',
  'Mud Room',
  'Closet',
  'Pantry',
  'Home Office',
  'Study',
  'Den',
  'Man Cave',
  'Bonus Room',
  'Sunroom',
  'Media Room',
  'TV Room',
  'Music Room',
  'Playroom',
  'Home Gym',
  'Sauna',
  'Hot Tub',
  'Pool Room',
  'Wine Cellar',
  'Wine Room',
  'Humidor',
  'Cellar',
  'Root Cellar',

  // ── Levels & spaces ───────────────────────────────────────────────────────
  'Basement',
  'Attic',
  'Crawl Space',
  'Garage',
  'Detached Garage',
  'Upstairs',
  'Downstairs',
  'Main Floor',
  'First Floor',
  'Second Floor',
  'Third Floor',
  'Lower Level',
  'Loft',
  'Stairwell',
  'Under House',

  // ── Outdoors ──────────────────────────────────────────────────────────────
  'Outside',
  'Outdoor',
  'Patio',
  'Porch',
  'Front Porch',
  'Back Porch',
  'Deck',
  'Backyard',
  'Garden',
  'Shed',
  'Workshop',
  'Pool House',
  'Guest House',
  'Carriage House',
  'Casita',
  'Cabana',
  'Well House',
  'Pump House',
  'Chicken Coop',
  'Dog House',
  'Kennel',
  'Barn',
  'Pole Barn',
  'Stable',

  // ── Whole properties ──────────────────────────────────────────────────────
  'Home',
  'House',
  'Apartment',
  'Condo',
  'Cabin',
  'Cottage',
  'Lake House',
  'Beach House',
  'Farmhouse',
  'Main House',
  'Guest Cabin',
  'Bunkhouse',
  'Clubhouse',
  'Lodge',
  'Ranch',
  'Church',
  'Sanctuary',

  // ── Vehicles ──────────────────────────────────────────────────────────────
  'RV',
  'Camper',
  'Travel Trailer',
  'Trailer',
  'Motorhome',
  'Airstream',
  'Van',
  'Truck',
  'Boat',
  'Engine Room',
  'Bilge',
  'Reefer Trailer',
  'Hangar',

  // ── Business & commercial ─────────────────────────────────────────────────
  'Office',
  'Front Office',
  'Break Room',
  'Conference Room',
  'Lobby',
  'Reception',
  'Warehouse',
  'Shop',
  'Store',
  'Storage Room',
  'Stock Room',
  'Supply Room',
  'Receiving',
  'Shipping',
  'Loading Dock',
  'Parking Garage',
  'Production Room',
  'Packaging Room',
  'Processing Room',
  'Packing Room',
  'Mixing Room',
  'Clean Room',
  'Drying Room',
  'Cure Room',
  'Bakery',
  'Restaurant',
  'Bar',
  'Salon',
  'Library',
  'Sales Floor',
  'Vault',
  'Safe',
  'Gun Safe',
  'Gun Room',
  'Winery',
  'Brewery',
  'Cheese Cave',

  // ── IT & mechanical ───────────────────────────────────────────────────────
  'Server Room',
  'Server Closet',
  'Server Rack',
  'Data Center',
  'Network Closet',
  'IT Room',
  'IT Closet',
  'Rack Room',
  'UPS Room',
  'Computer Room',
  'Electrical Room',
  'Mechanical Room',
  'Machine Room',
  'Boiler Room',
  'Furnace Room',
  'Compressor Room',
  'Equipment Room',
  'Utility Room',
  'Pump Room',
  'Water Room',

  // ── Medical, pharmacy & lab ───────────────────────────────────────────────
  'Pharmacy',
  'Pharmacy Fridge',
  'Pharmacy Freezer',
  'Vaccine Fridge',
  'Vaccine Freezer',
  'Medication Fridge',
  'Med Room',
  'Med Fridge',
  'Blood Fridge',
  'Insulin Fridge',
  'Sample Fridge',
  'Specimen Fridge',
  'Lab',
  'Lab Fridge',
  'Lab Freezer',
  'Operating Room',
  'Procedure Room',
  'Recovery Room',
  'Exam Room',
  'Treatment Room',
  'Ante Room',
  'Sterile Storage',
  'Sterile Processing',
  'Clinic Fridge',
  'Incubator',

  // ── Growing ───────────────────────────────────────────────────────────────
  'Greenhouse',
  'Grow Room',
  'Grow Tent',
  'Hoop House',
  'High Tunnel',
  'Veg Room',
  'Flower Room',
  'Seedling Room',
  'Plants',
  'Aquarium',
  'Terrarium',
  'Reptile Room',
  'Beehive',

  // ── Fire sprinkler ────────────────────────────────────────────────────────
  'Riser Room',
  'Sprinkler Riser',
  'Branch Line',
];

const normalize = value => String(value == null ? '' : value).trim().toLowerCase();

// De-dupe while keeping the first occurrence, so the thematic ordering above
// (and the selective-first ordering below) survives.
const uniqueNames = names => {
  const seen = new Set();
  const out = [];

  names.forEach(name => {
    const key = normalize(name);

    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    out.push(name);
  });

  return out;
};

export const getQuickPicks = (isSelective = false) => {
  return isSelective ? SELECTIVE_QUICK_PICKS.slice() : QUICK_PICKS.slice();
};

export const getSuggestionPool = (isSelective = false) => {
  return isSelective
    ? uniqueNames([...SELECTIVE_SUGGESTIONS, ...SUGGESTIONS])
    : uniqueNames(SUGGESTIONS);
};

/**
 * Autocomplete over the suggestion pool.
 *
 * Ranking: names that start with the query beat names with a word starting
 * with the query, which beat plain substring matches. Ties keep pool order.
 *
 * @param {string} query
 * @param {{isSelective?: boolean, limit?: number, extra?: string[]}} options
 *        `extra` is merged into the pool ahead of the curated names — used to
 *        surface names the account already uses.
 * @returns {string[]}
 */
export const searchSensorNames = (query, options = {}) => {
  const { isSelective = false, limit = 8, extra = [] } = options;

  const pool = uniqueNames([...extra, ...getSuggestionPool(isSelective)]);
  const q = normalize(query);

  if (!q) {
    return pool.slice(0, limit);
  }

  const startsWith = [];
  const wordStart = [];
  const contains = [];

  pool.forEach(name => {
    const key = normalize(name);
    const at = key.indexOf(q);

    if (at === -1) {
      return;
    }

    if (at === 0) {
      startsWith.push(name);
    } else if (/[\s\-]/.test(key.charAt(at - 1))) {
      wordStart.push(name);
    } else {
      contains.push(name);
    }
  });

  return [...startsWith, ...wordStart, ...contains].slice(0, limit);
};

/**
 * Whether `name` is already used by one of `takenNames` (case-insensitive).
 */
export const isNameTaken = (name, takenNames = []) => {
  const key = normalize(name);

  if (!key) {
    return false;
  }

  return takenNames.some(taken => normalize(taken) === key);
};

/**
 * First unused numbered variant of `name` — "Freezer" becomes "Freezer 2",
 * then "Freezer 3", and so on. Returns `name` untouched when it's free.
 */
export const nextAvailableName = (name, takenNames = []) => {
  const base = String(name == null ? '' : name).trim();

  if (!base || !isNameTaken(base, takenNames)) {
    return base;
  }

  // Strip an existing trailing counter so "Freezer 2" doesn't become
  // "Freezer 2 2".
  const stripped = base.replace(/\s+\d+$/, '').trim() || base;

  for (let i = 2; i < 100; i++) {
    const candidate = `${stripped} ${i}`;

    if (!isNameTaken(candidate, takenNames)) {
      return candidate;
    }
  }

  return base;
};

export default SUGGESTIONS;
